import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Camera,
  Upload,
  Sparkles,
  RotateCw,
  Check,
  Tag,
  AlertCircle,
  Scissors,
  Eye,
  RefreshCw,
  Flashlight,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { ClothingItem, CategoryType } from '../types';
import { api } from '../lib/api';
import { compressImage, autoCropGarment, fileToDataUrl, cropVideoToPortrait, rotateImageData90 } from '../lib/imageUtils';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onItemAdded: (item: ClothingItem) => void;
  hasGeminiKey: boolean;
}

const CATEGORY_OPTIONS: { key: CategoryType; label: string }[] = [
  { key: 'tops', label: 'Tops (T-Shirts, Shirts, Hoodies)' },
  { key: 'bottoms', label: 'Bottoms (Jeans, Pants, Shorts)' },
  { key: 'outerwear', label: 'Jackets & Outerwear' },
  { key: 'shoes', label: 'Shoes & Footwear' },
  { key: 'accessories', label: 'Accessories (Hats, Belts, Bags)' },
];

export const AddItemModal: React.FC<AddItemModalProps> = ({
  isOpen,
  onClose,
  onItemAdded,
  hasGeminiKey,
}) => {
  const [activeSide, setActiveSide] = useState<'front' | 'back'>('front');
  const [imageFront, setImageFront] = useState<string>('');
  const [imageBack, setImageBack] = useState<string>('');
  const [imageCroppedFront, setImageCroppedFront] = useState<string>('');
  const [imageCroppedBack, setImageCroppedBack] = useState<string>('');
  const [useCropped, setUseCropped] = useState<boolean>(true);

  // Form inputs
  const [name, setName] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [category, setCategory] = useState<CategoryType>('tops');
  const [subcategory, setSubcategory] = useState<string>('');
  const [color, setColor] = useState<string>('');
  const [pattern, setPattern] = useState<string>('Solid');
  const [season, setSeason] = useState<string>('All-Season');
  const [occasion, setOccasion] = useState<string>('Casual');
  const [fitType, setFitType] = useState<string>('Regular');
  const [material, setMaterial] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  // Status & Camera
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string>('');
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>('environment');
  const [hasTorch, setHasTorch] = useState<boolean>(false);
  const [torchOn, setTorchOn] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Start Camera with resilient fallback constraints
  const startCamera = async () => {
    stopCamera();
    setCameraLoading(true);
    setAnalysisError('');

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: cameraFacing },
          aspectRatio: { ideal: 0.75 },
          width: { ideal: 1080 },
          height: { ideal: 1440 },
        },
        audio: false,
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (fallbackErr) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: cameraFacing },
            aspectRatio: { ideal: 0.75 },
          },
          audio: false,
        });
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('Autoplay error:', playErr);
        }
      }

      setIsCameraActive(true);

      // Check torch capability
      const track = stream.getVideoTracks()[0];
      if (track) {
        const capabilities = (track.getCapabilities?.() as any) || {};
        setHasTorch('torch' in capabilities);
      }
    } catch (err: any) {
      console.warn('Camera error:', err);
      setAnalysisError('Camera access failed: ' + (err?.message || 'Check camera permissions.'));
      setIsCameraActive(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setTorchOn(false);
  };

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    try {
      await (track as any).applyConstraints({
        advanced: [{ torch: !torchOn }],
      });
      setTorchOn(!torchOn);
    } catch (e) {
      console.warn('Torch failed', e);
    }
  };

  const toggleCameraFacing = () => {
    const nextFacing = cameraFacing === 'environment' ? 'user' : 'environment';
    setCameraFacing(nextFacing);
  };

  // When camera active or stream changes, ensure video plays
  useEffect(() => {
    if (isCameraActive && streamRef.current && videoRef.current) {
      if (videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(console.warn);
      }
    }
  }, [isCameraActive, cameraFacing]);

  // Restart camera when facing mode changes
  useEffect(() => {
    if (isCameraActive) {
      startCamera();
    }
  }, [cameraFacing]);

  // Cleanup camera on close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Take Snapshot from Camera (Enforce 3:4 portrait)
  const takeSnapshot = async () => {
    if (!videoRef.current) return;
    try {
      const rawData = cropVideoToPortrait(videoRef.current, 3 / 4, 0.88);
      if (!rawData) throw new Error('Failed to crop video frame');
      const compressed = await compressImage(rawData, 1200, 0.82);

      if (activeSide === 'front') {
        setImageFront(compressed);
        setImageCroppedFront('');
      } else {
        setImageBack(compressed);
        setImageCroppedBack('');
      }
      stopCamera();
    } catch (err) {
      setAnalysisError('Failed to capture snapshot.');
    }
  };

  // Rotate active photo 90 degrees
  const handleRotatePhoto = async () => {
    const currentImg = activeSide === 'front' ? imageFront : imageBack;
    if (!currentImg) return;
    try {
      const rotated = await rotateImageData90(currentImg);
      if (activeSide === 'front') {
        setImageFront(rotated);
        setImageCroppedFront('');
      } else {
        setImageBack(rotated);
        setImageCroppedBack('');
      }
    } catch (err) {
      console.warn('Rotate failed:', err);
    }
  };

  // File Upload Handler (Native file input)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAnalysisError('');

    try {
      const rawData = await fileToDataUrl(file);
      const compressed = await compressImage(rawData, 1200, 0.82);

      if (activeSide === 'front') {
        setImageFront(compressed);
      } else {
        setImageBack(compressed);
      }
      stopCamera();
    } catch (err: any) {
      setAnalysisError('Failed to process image: ' + (err?.message || 'Try a different photo.'));
    } finally {
      e.target.value = '';
    }
  };

  // Run Google AI Vision to scan Brand, Size, Category, and Auto-Crop
  const handleAIScan = async () => {
    if (!imageFront) {
      setAnalysisError('Please take or upload the front photo of your clothing first.');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError('');

    try {
      const res = await api.ai.analyze({
        image_front: imageFront,
        image_back: imageBack || undefined,
      });

      const data = res.analysis;

      // Auto-fill form fields
      if (data.name) setName(data.name);
      if (data.brand) setBrand(data.brand);
      if (data.size) setSize(data.size);
      if (data.category) setCategory(data.category);
      if (data.subcategory) setSubcategory(data.subcategory);
      if (data.color) setColor(data.color);
      if (data.pattern) setPattern(data.pattern);
      if (data.season) setSeason(data.season);
      if (data.occasion) setOccasion(data.occasion);
      if (data.fit_type) setFitType(data.fit_type);
      if (data.material) setMaterial(data.material);
      if (data.styling_tip) {
        setNotes((prev) => (prev ? `${prev}\n\nAI Stylist: ${data.styling_tip}` : `AI Stylist: ${data.styling_tip}`));
      }

      // Auto-crop front image
      if (data.box_2d && Array.isArray(data.box_2d)) {
        try {
          const croppedFront = await autoCropGarment(imageFront, data.box_2d);
          setImageCroppedFront(croppedFront);
        } catch (cropErr) {
          console.warn('Auto-crop front failed', cropErr);
        }
      }

      // Auto-crop back image if available
      if (imageBack && data.box_2d_back && Array.isArray(data.box_2d_back)) {
        try {
          const croppedBack = await autoCropGarment(imageBack, data.box_2d_back);
          setImageCroppedBack(croppedBack);
        } catch (cropErr) {
          console.warn('Auto-crop back failed', cropErr);
        }
      }
    } catch (err: any) {
      setAnalysisError(err?.message || 'AI recognition failed. You can still fill in details manually.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Submit to D1 Database
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageFront) {
      setAnalysisError('A front photo is required.');
      return;
    }
    if (!name.trim()) {
      setAnalysisError('Please enter an item name.');
      return;
    }

    setIsSubmitting(true);
    setAnalysisError('');

    try {
      const res = await api.clothes.create({
        name: name.trim(),
        brand: brand.trim(),
        size: size.trim(),
        category,
        subcategory: subcategory.trim(),
        color: color.trim(),
        pattern,
        season,
        occasion,
        fit_type: fitType,
        material: material.trim(),
        image_front: imageFront,
        image_back: imageBack || undefined,
        image_cropped: useCropped && imageCroppedFront ? imageCroppedFront : imageFront,
        image_cropped_back: useCropped && imageCroppedBack ? imageCroppedBack : imageBack || undefined,
        is_favorite: isFavorite ? 1 : 0,
        notes: notes.trim(),
      });

      onItemAdded(res.item);
      stopCamera();
      onClose();
    } catch (err: any) {
      setAnalysisError(err?.message || 'Failed to save item.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const activePhoto = activeSide === 'front'
    ? (useCropped && imageCroppedFront ? imageCroppedFront : imageFront)
    : (useCropped && imageCroppedBack ? imageCroppedBack : imageBack);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
              <Camera className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Add New Clothing Item</h3>
              <p className="text-xs text-slate-400">Front &amp; back photos, brand, size, and AI auto-crop</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {analysisError && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{analysisError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Side Selector Tabs (Front vs Back) */}
          <div className="flex items-center justify-center p-1 bg-slate-950 rounded-2xl border border-slate-800 max-w-md mx-auto">
            <button
              type="button"
              onClick={() => {
                stopCamera();
                setActiveSide('front');
              }}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                activeSide === 'front'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <span>1. Front Photo</span>
              {imageFront ? (
                <span className="w-4 h-4 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center text-[10px] font-black">
                  ✓
                </span>
              ) : (
                <span className="text-[10px] text-amber-400 font-normal">(Required)</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                stopCamera();
                setActiveSide('back');
              }}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                activeSide === 'back'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <span>2. Back Photo</span>
              {imageBack ? (
                <span className="w-4 h-4 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center text-[10px] font-black">
                  ✓
                </span>
              ) : (
                <span className="text-[10px] text-slate-500 font-normal">(Optional)</span>
              )}
            </button>
          </div>

          {/* Viewfinder / Display Area */}
          <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 aspect-[4/3] max-w-md mx-auto flex flex-col items-center justify-center shadow-inner">
            {/* Permanent Video Element */}
            <video
              ref={videoRef}
              playsInline
              autoPlay
              muted
              // @ts-ignore
              webkit-playsinline="true"
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(console.warn);
                }
              }}
              className={`w-full h-full object-cover ${
                isCameraActive ? 'block' : 'hidden'
              }`}
            />

            {/* Live Camera Viewfinder Overlay Controls */}
            {isCameraActive && (
              <div className="absolute inset-0 flex flex-col justify-between p-3 pointer-events-none">
                <div className="flex items-center justify-between pointer-events-auto">
                  <span className="px-2.5 py-1 rounded-full bg-slate-950/80 text-emerald-400 border border-emerald-500/30 text-[11px] font-mono font-bold">
                    PORTRAIT: {activeSide.toUpperCase()}
                  </span>

                  <div className="flex items-center gap-1.5">
                    {hasTorch && (
                      <button
                        type="button"
                        onClick={toggleTorch}
                        className={`p-2 rounded-full backdrop-blur-md shadow-md transition-all pointer-events-auto ${
                          torchOn ? 'bg-amber-400 text-slate-950 font-bold' : 'bg-slate-900/80 text-white'
                        }`}
                        title="Toggle Flashlight"
                      >
                        <Flashlight className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={toggleCameraFacing}
                      className="p-2 rounded-full bg-slate-900/80 text-white backdrop-blur-md border border-white/20 shadow-md pointer-events-auto active:scale-95"
                      title="Flip Lens"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Shutter Bar */}
                <div className="flex items-center justify-center gap-4 pointer-events-auto pb-1">
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="p-3 rounded-full bg-slate-900/80 text-slate-300 hover:text-white border border-slate-700 shadow-md"
                    title="Cancel Camera"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <button
                    type="button"
                    onClick={takeSnapshot}
                    className="w-14 h-14 rounded-full bg-white text-slate-950 flex items-center justify-center shadow-2xl ring-4 ring-brand-500/50 hover:scale-105 active:scale-95 transition-transform"
                    title="Take Photo"
                  >
                    <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-white" />
                  </button>
                </div>
              </div>
            )}

            {/* Spinner when camera is starting */}
            {cameraLoading && (
              <div className="absolute inset-0 bg-slate-950/80 flex flex-col items-center justify-center gap-2 text-slate-300 z-10">
                <RefreshCw className="w-7 h-7 text-brand-400 animate-spin" />
                <span className="text-xs font-semibold">Opening camera feed...</span>
              </div>
            )}

            {/* Captured / Uploaded Image View */}
            {!isCameraActive && activePhoto && (
              <div className="relative w-full h-full group">
                <img
                  src={activePhoto}
                  alt={activeSide}
                  className="w-full h-full object-contain p-2"
                />

                <div className="absolute top-3 left-3 bg-slate-900/85 backdrop-blur-md px-2.5 py-1 rounded-lg text-xs font-bold text-white border border-white/10 flex items-center gap-1.5 shadow-md">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>{activeSide === 'front' ? 'Front Photo Ready' : 'Back Photo Ready'}</span>
                </div>

                <div className="absolute bottom-3 inset-x-3 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleRotatePhoto}
                    className="px-2.5 py-1.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-white text-xs font-semibold shadow-md border border-slate-700 backdrop-blur-md transition-colors flex items-center gap-1.5"
                    title="Rotate photo 90 degrees"
                  >
                    <RefreshCw className="w-3.5 h-3.5 text-brand-400" />
                    <span>Rotate 90°</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (activeSide === 'front') {
                        setImageFront('');
                        setImageCroppedFront('');
                      } else {
                        setImageBack('');
                        setImageCroppedBack('');
                      }
                    }}
                    className="px-3 py-1.5 rounded-xl bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold shadow-lg backdrop-blur-md transition-colors"
                  >
                    Retake / Replace
                  </button>
                </div>
              </div>
            )}

            {/* Empty State: Take Photo or Upload */}
            {!isCameraActive && !activePhoto && (
              <div className="text-center p-6 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto text-slate-400 border border-slate-800">
                  <Camera className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-200">
                    Add {activeSide.toUpperCase()} Photo
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Lay clothing flat on a clean surface or hang it up
                  </p>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={startCamera}
                    className="inline-flex items-center space-x-1.5 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-md shadow-brand-600/30 transition-all hover:scale-105 active:scale-95"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Open Camera</span>
                  </button>

                  {/* Native Label File Upload for 100% Mobile Reliability */}
                  <label className="inline-flex items-center space-x-1.5 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-200 text-xs font-bold border border-slate-700 transition-colors cursor-pointer active:scale-95">
                    <Upload className="w-4 h-4 text-brand-400" />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Quick Front & Back Thumbnails Row */}
          {(imageFront || imageBack) && (
            <div className="bg-slate-950 p-2.5 rounded-2xl border border-slate-800/80 flex items-center justify-between gap-2 max-w-md mx-auto">
              <div className="flex items-center gap-2">
                {/* Front Thumbnail Pill */}
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    setActiveSide('front');
                  }}
                  className={`flex items-center gap-2 p-1.5 rounded-xl border transition-all ${
                    activeSide === 'front'
                      ? 'border-brand-500 bg-brand-500/15'
                      : 'border-slate-800 bg-slate-900/60 opacity-80'
                  }`}
                >
                  {imageFront ? (
                    <img
                      src={imageCroppedFront || imageFront}
                      alt="Front"
                      className="w-10 h-10 object-cover rounded-lg bg-slate-900"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-slate-500 text-xs font-bold">
                      +
                    </div>
                  )}
                  <div className="text-left pr-1">
                    <span className="text-[11px] font-bold text-white block">Front</span>
                    <span className={`text-[9px] ${imageFront ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {imageFront ? 'Ready ✓' : 'Empty'}
                    </span>
                  </div>
                </button>

                {/* Back Thumbnail Pill */}
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    setActiveSide('back');
                  }}
                  className={`flex items-center gap-2 p-1.5 rounded-xl border transition-all ${
                    activeSide === 'back'
                      ? 'border-brand-500 bg-brand-500/15'
                      : 'border-slate-800 bg-slate-900/60 opacity-80'
                  }`}
                >
                  {imageBack ? (
                    <img
                      src={imageCroppedBack || imageBack}
                      alt="Back"
                      className="w-10 h-10 object-cover rounded-lg bg-slate-900"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-slate-500 text-xs font-bold">
                      +
                    </div>
                  )}
                  <div className="text-left pr-1">
                    <span className="text-[11px] font-bold text-white block">Back</span>
                    <span className={`text-[9px] ${imageBack ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {imageBack ? 'Ready ✓' : 'Optional'}
                    </span>
                  </div>
                </button>
              </div>

              {!imageBack && (
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    setActiveSide('back');
                  }}
                  className="text-xs text-brand-400 hover:text-brand-300 font-semibold"
                >
                  + Add Back Side
                </button>
              )}
            </div>
          )}

          {/* AI Auto-Crop & Recognition Trigger */}
          {imageFront && (
            <div className="bg-gradient-to-r from-brand-950/40 via-indigo-950/40 to-slate-950 p-4 rounded-2xl border border-brand-500/30 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 rounded-xl bg-brand-500/20 text-brand-300 border border-brand-500/30">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                    AI Garment Scanner &amp; Auto-Crop
                  </h4>
                  <p className="text-[11px] text-slate-400">
                    Auto-detects Brand, Size, Category, Color &amp; crops background clutter
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleAIScan}
                  disabled={isAnalyzing}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg shadow-brand-500/20 transition-all hover:scale-105 disabled:opacity-50"
                >
                  {isAnalyzing ? (
                    <>
                      <RotateCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Scanning...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Auto-Fill with AI</span>
                    </>
                  )}
                </button>

                {imageCroppedFront && (
                  <button
                    type="button"
                    onClick={() => setUseCropped(!useCropped)}
                    className="p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white border border-slate-700 text-xs font-semibold flex items-center gap-1"
                    title="Toggle cropped vs original photo"
                  >
                    <Scissors className="w-3.5 h-3.5 text-brand-400" />
                    <span>{useCropped ? 'Cropped' : 'Original'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Garment Name *
              </label>
              <input
                type="text"
                placeholder="e.g. Vintage Heavyweight Boxy Tee"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Brand */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Brand Name</span>
                <span className="text-[10px] text-brand-400 lowercase font-normal">Nike, Zara, Carhartt...</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Nike, Carhartt, Zara, Uniqlo"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Size */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Size</span>
                <span className="text-[10px] text-brand-400 lowercase font-normal">M, L, 32x30, US 10.5...</span>
              </label>
              <input
                type="text"
                placeholder="e.g. M, L, XL, 32x30, US 10"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Category *
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as CategoryType)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Style / Type
              </label>
              <input
                type="text"
                placeholder="e.g. Hoodie, Cargo Pants, Sneakers"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Primary Color
              </label>
              <input
                type="text"
                placeholder="e.g. Washed Black, Olive, White"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {/* Season */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Season
              </label>
              <select
                value={season}
                onChange={(e) => setSeason(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              >
                <option value="All-Season">All-Season</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
                <option value="Fall">Fall / Autumn</option>
                <option value="Winter">Winter</option>
              </select>
            </div>

            {/* Occasion */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Occasion
              </label>
              <select
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              >
                <option value="Casual">Casual</option>
                <option value="Streetwear">Streetwear</option>
                <option value="Smart Casual">Smart Casual</option>
                <option value="Formal">Formal</option>
                <option value="Gym/Sport">Gym / Sport</option>
                <option value="Date Night">Date Night</option>
                <option value="Lounge">Lounge / Home</option>
              </select>
            </div>

            {/* Fit Type */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Fit Cut
              </label>
              <select
                value={fitType}
                onChange={(e) => setFitType(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              >
                <option value="Regular">Regular Fit</option>
                <option value="Slim">Slim Fit</option>
                <option value="Oversized">Oversized / Boxy</option>
                <option value="Baggy">Baggy / Relaxed</option>
                <option value="Cropped">Cropped</option>
              </select>
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Styling Notes or Details
              </label>
              <textarea
                rows={2}
                placeholder="Care instructions, pairing notes, or AI tips..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="pt-4 border-t border-slate-800 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !imageFront || !name.trim()}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-sm font-bold shadow-lg shadow-brand-500/30 transition-all hover:scale-105 disabled:opacity-50 disabled:pointer-events-none"
            >
              {isSubmitting ? 'Saving to Wardrobe...' : 'Save to Closet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
